import { useContext, useState } from 'react'
import { ProgramContext } from 'context/ProgramContext'
import { DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid'

import ContentFrame from 'components/Frame/ContentFrame'
import MicroVolumeStimulusTypeDropdown from 'components/Program/MicroVolumeConfig/MicroVolumeStimulusTypeDropdown'
import { Switch } from '@headlessui/react'
import NanoVolumeWorkoutSlotConfigEditor from 'components/Program/NanoVolumeWorkoutSlotConfig/NanoVolumeWorkoutSlotConfigEditor'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const inputClassName = 'block w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-400 focus:border-transparent'
const selectClassName = 'block w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-400 focus:border-transparent'

const TAPER_LEVEL_OPTIONS = [
    { value: 'taper-none', label: 'Full' },
    { value: 'taper-one', label: 'Height 1' },
    { value: 'taper-two', label: 'Height 2' },
    { value: 'taper-three', label: 'Height 3' },
]

export default function MicroVolumeConfigEditor({ 
    microVolumeConfigIndex, 
    microVolumeConfig,
    DuplicateSelectedMicroVolumeConfig = () => {},
    DeleteSelectedMicroVolumeConfig = () => {}
}) {
    const { MicroVolumeConfigSubContextData } = useContext(ProgramContext)
    const { HandleMicroVolumeConfigDraftChange } = MicroVolumeConfigSubContextData

    const [taperExpanded, setTaperExpanded] = useState(false)
    const taperEnabled = microVolumeConfig.useTaper === 1

    const ToggleTaperExpanded = () => {
        if (!taperEnabled) return
        setTaperExpanded(!taperExpanded)
    }

    const HandleTaperToggle = () => {
        const nextUseTaper = taperEnabled ? 0 : 1

        if (nextUseTaper === 0) {
            setTaperExpanded(false)
        }

        HandleMicroVolumeConfigDraftChange(microVolumeConfigIndex, 'useTaper', nextUseTaper)
    }

    // console.log(microVolumeConfig)

    return (
        <ContentFrame>
            <div className="w-full p-4 space-y-4">
                <div className='flex items-center gap-x-4'>
                    <p className="text-sm text-left flex-1">
                        Week {microVolumeConfigIndex + 1}
                    </p>

                    <button
                        type="button"
                        title="Duplicate Micro Volume Config"
                        className="-mt-1 -mr-1 h-5 w-5 flex justify-center items-center text-gray-500"
                        onClick={DuplicateSelectedMicroVolumeConfig}
                    >
                        <DocumentDuplicateIcon className="h-5 pointer-events-none" />
                    </button>

                    <button
                        type="button"
                        title="Delete Micro Volume Config"
                        className="-mt-1 mr-1 h-5 w-5 flex justify-center items-center text-gray-500"
                        onClick={DeleteSelectedMicroVolumeConfig}
                    >
                        <TrashIcon className="h-5 pointer-events-none" />
                    </button>
                </div>

                <MicroVolumeStimulusTypeDropdown
                    microVolumeStimulusTypeId={microVolumeConfig.microVolumeStimulusTypeId}
                    setMicroVolumeStimulusTypeId={(value) => HandleMicroVolumeConfigDraftChange(microVolumeConfigIndex, 'microVolumeStimulusTypeId', value)}
                />  

                <div className='border border-gray-300 rounded-lg p-2 space-y-2'>
                    <div className='flex items-center gap-x-4'>
                        <p
                            className={classNames(
                                'flex-grow select-none text-sm pl-1',
                                taperEnabled ? 'cursor-pointer' : 'cursor-not-allowed'
                            )}
                            onClick={ToggleTaperExpanded}
                        >
                            Apply Taper
                        </p>

                        <Switch
                            checked={taperEnabled}
                            className={classNames(
                                taperEnabled ? 'bg-orange-600' : 'bg-gray-200',
                                '-mr-2 relative inline-flex h-4 w-8 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-2'
                            )}
                            onChange={HandleTaperToggle}
                        >
                            <span className="sr-only">Use setting</span>
                            <span
                                aria-hidden="true"
                                className={classNames(
                                    taperEnabled ? 'translate-x-4' : 'translate-x-0',
                                    'pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                )}
                            />
                        </Switch>

                        <button
                            type="button"
                            title={taperEnabled ? (taperExpanded ? 'Collapse taper settings' : 'Expand taper settings') : 'Enable taper to expand settings'}
                            className={classNames(
                                'h-5 w-5 flex items-center justify-center',
                                taperEnabled ? 'cursor-pointer text-gray-600' : 'cursor-not-allowed text-gray-300'
                            )}
                            onClick={ToggleTaperExpanded}
                            disabled={!taperEnabled}
                        >
                            {taperExpanded
                                ? <ChevronUpIcon className='w-5 h-5' />
                                : <ChevronDownIcon className='w-5 h-5' />
                            }
                        </button>
                    </div>

                    {taperExpanded && taperEnabled &&
                        <div className="w-full space-y-4 px-1">
                            <p className='text-xs text-gray-500'>
                                Load adjustments up are disabled when taper is enabled
                            </p>

                            <div className='space-y-1'>
                                <p className='pl-1 text-xs'>Display Height</p>

                                <select
                                    id="taperLevel"
                                    className={selectClassName}
                                    value={microVolumeConfig.taperLevel}
                                    onChange={(event) => HandleMicroVolumeConfigDraftChange(microVolumeConfigIndex, 'taperLevel', event.target.value)}
                                >
                                    {TAPER_LEVEL_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className='space-y-1'>
                                <p className='pl-1 text-xs'>
                                    Load Dampener Amount (set in decimal)
                                </p>
                                
                                <p className='pl-1 text-xs text-gray-500'>
                                    Example: 0.8 would mean load is tapered by 20% (0.8 of full load)
                                </p>

                                <input
                                    type="number"
                                    id="taperDampener"
                                    step="0.001"
                                    className={inputClassName}
                                    placeholder="e.g. 1.0"
                                    value={microVolumeConfig.taperDampener}
                                    onChange={(event) => HandleMicroVolumeConfigDraftChange(microVolumeConfigIndex, 'taperDampener', event.target.value === '' ? '' : parseFloat(event.target.value))}
                                />
                            </div>

                            <div className='space-y-1'>
                                <p className='pl-1 text-xs'>Exercise Regression</p>

                                <input
                                    type="number"
                                    id="exerciseRegressionLevel"
                                    min="0"
                                    step="1"
                                    className={inputClassName}
                                    placeholder="e.g. 0"
                                    value={microVolumeConfig.exerciseRegressionLevel}
                                    onChange={(event) => HandleMicroVolumeConfigDraftChange(microVolumeConfigIndex, 'exerciseRegressionLevel', event.target.value === '' ? '' : parseInt(event.target.value, 10))}
                                />
                            </div>
                        </div>
                    }
                </div>

                <div className='space-y-4'>
                    {microVolumeConfig?.workoutConfigs?.map((workoutConfig, workoutConfigIndex) => (
                        <div 
                            key={workoutConfigIndex} 
                            className='w-full divide-y divide-gray-300 border border-gray-300 rounded-lg'
                        >
                            <p className='text-sm text-left p-2 bg-gray-300'>
                                {workoutConfig.name}
                            </p>

                            <div className='grid grid-cols-5 bg-gray-200 py-0.5'>
                                <p className='col-span-3 text-xs text-left px-2 text-gray-500'>Volume overrides</p>
                                <p className='text-xs text-center px-2 text-gray-500'>unilat</p>
                                <p className='text-xs text-center px-2 text-gray-500'>bilat</p>
                            </div>

                            {/* Bodyweight overrides */}

                            <div className='grid grid-cols-5 bg-gray-200 flex items-center'>
                                <p className='col-span-3 text-xs text-left px-2 text-gray-500'>Bodyweight reps</p>

                                <input
                                    type="number"
                                    className="py-1 w-14 border-l border-gray-300 text-center text-sm bg-gray-200 font-bold placeholder:text-gray-900 placeholder:font-bold [&::-webkit-inner-spin-button]:block [&::-webkit-inner-spin-button]:opacity-100"
                                    placeholder="0"
                                    value={workoutConfig.bodyweightUnilateralRepsOverride}
                                    onChange={(event) => {
                                        HandleMicroVolumeConfigDraftChange(
                                            microVolumeConfigIndex,
                                            'bodyweightUnilateralRepsOverride',
                                            { workoutConfigIndex, reps: event.target.value === '' ? '' : parseInt(event.target.value, 10) }
                                        )
                                    }}
                                />

                                <input
                                    type="number"
                                    className="py-1 w-14 border-l border-gray-300 text-center text-sm bg-gray-200 font-bold placeholder:text-gray-900 placeholder:font-bold [&::-webkit-inner-spin-button]:block [&::-webkit-inner-spin-button]:opacity-100"
                                    placeholder="0"
                                    value={workoutConfig.bodyweightBilateralRepsOverride}
                                    onChange={(event) => {
                                        HandleMicroVolumeConfigDraftChange(
                                            microVolumeConfigIndex,
                                            'bodyweightBilateralRepsOverride',
                                            { workoutConfigIndex, reps: event.target.value === '' ? '' : parseInt(event.target.value, 10) }
                                        )
                                    }}
                                />
                            </div>
                            
                            {/* High Volume Plyos */}

                            <div className='grid grid-cols-5 bg-gray-200 flex items-center'>
                                <p className='col-span-3 text-xs text-left px-2 text-gray-500'>High Volume Plyo Reps</p>

                                <input
                                    type="number"
                                    className="py-1 w-14 border-l border-gray-300 text-center text-sm bg-gray-200 font-bold placeholder:text-gray-900 placeholder:font-bold [&::-webkit-inner-spin-button]:block [&::-webkit-inner-spin-button]:opacity-100"
                                    placeholder="0"
                                    value={workoutConfig.highVolumePlyometricUnilateralRepsOverride}
                                    onChange={(event) => {
                                        HandleMicroVolumeConfigDraftChange(
                                            microVolumeConfigIndex,
                                            'highVolumePlyometricUnilateralRepsOverride',
                                            { workoutConfigIndex, reps: event.target.value === '' ? '' : parseInt(event.target.value, 10) }
                                        )
                                    }}
                                />

                                <input
                                    type="number"
                                    className="py-1 w-14 border-l border-gray-300 text-center text-sm bg-gray-200 font-bold placeholder:text-gray-900 placeholder:font-bold [&::-webkit-inner-spin-button]:block [&::-webkit-inner-spin-button]:opacity-100"
                                    placeholder="0"
                                    value={workoutConfig.highVolumePlyometricBilateralRepsOverride}
                                    onChange={(event) => {
                                        HandleMicroVolumeConfigDraftChange(
                                            microVolumeConfigIndex,
                                            'highVolumePlyometricBilateralRepsOverride',
                                            { workoutConfigIndex, reps: event.target.value === '' ? '' : parseInt(event.target.value, 10) }
                                        )
                                    }}
                                />
                            </div>

                            {/* Med Volume Plyos */}

                            <div className='grid grid-cols-5 bg-gray-200 flex items-center'>
                                <p className='col-span-3 text-xs text-left px-2 text-gray-500'>Med Volume Plyo Reps</p>

                                <input
                                    type="number"
                                    className="py-1 w-14 border-l border-gray-300 text-center text-sm bg-gray-200 font-bold placeholder:text-gray-900 placeholder:font-bold [&::-webkit-inner-spin-button]:block [&::-webkit-inner-spin-button]:opacity-100"
                                    placeholder="0"
                                    value={workoutConfig.mediumVolumePlyometricUnilateralRepsOverride}
                                    onChange={(event) => {
                                        HandleMicroVolumeConfigDraftChange(
                                            microVolumeConfigIndex,
                                            'mediumVolumePlyometricUnilateralRepsOverride',
                                            { workoutConfigIndex, reps: event.target.value === '' ? '' : parseInt(event.target.value, 10) }
                                        )
                                    }}
                                />

                                <input
                                    type="number"
                                    className="py-1 w-14 border-l border-gray-300 text-center text-sm bg-gray-200 font-bold placeholder:text-gray-900 placeholder:font-bold [&::-webkit-inner-spin-button]:block [&::-webkit-inner-spin-button]:opacity-100"
                                    placeholder="0"
                                    value={workoutConfig.mediumVolumePlyometricBilateralRepsOverride}
                                    onChange={(event) => {
                                        HandleMicroVolumeConfigDraftChange(
                                            microVolumeConfigIndex,
                                            'mediumVolumePlyometricBilateralRepsOverride',
                                            { workoutConfigIndex, reps: event.target.value === '' ? '' : parseInt(event.target.value, 10) }
                                        )
                                    }}
                                />
                            </div>

                            {/* Low Volume Plyos */}

                            <div className='grid grid-cols-5 bg-gray-200 flex items-center'>
                                <p className='col-span-3 text-xs text-left px-2 text-gray-500'>Low Volume Plyo Reps</p>

                                <input
                                    type="number"
                                    className="py-1 w-14 border-l border-gray-300 text-center text-sm bg-gray-200 font-bold placeholder:text-gray-900 placeholder:font-bold [&::-webkit-inner-spin-button]:block [&::-webkit-inner-spin-button]:opacity-100"
                                    placeholder="0"
                                    value={workoutConfig.lowVolumePlyometricUnilateralRepsOverride}
                                    onChange={(event) => {
                                        HandleMicroVolumeConfigDraftChange(
                                            microVolumeConfigIndex,
                                            'mediumVolumePlyometricUnilateralRepsOverride',
                                            { workoutConfigIndex, reps: event.target.value === '' ? '' : parseInt(event.target.value, 10) }
                                        )
                                    }}
                                />

                                <input
                                    type="number"
                                    className="py-1 w-14 border-l border-gray-300 text-center text-sm bg-gray-200 font-bold placeholder:text-gray-900 placeholder:font-bold [&::-webkit-inner-spin-button]:block [&::-webkit-inner-spin-button]:opacity-100"
                                    placeholder="0"
                                    value={workoutConfig.lowVolumePlyometricBilateralRepsOverride}
                                    onChange={(event) => {
                                        HandleMicroVolumeConfigDraftChange(
                                            microVolumeConfigIndex,
                                            'mediumVolumePlyometricBilateralRepsOverride',
                                            { workoutConfigIndex, reps: event.target.value === '' ? '' : parseInt(event.target.value, 10) }
                                        )
                                    }}
                                />
                            </div>

                            {/* Seconds overrides */}

                            <div className='grid grid-cols-5 bg-gray-200 flex items-center'>
                                <p className='col-span-3 text-xs text-left px-2 text-gray-500'>Seconds</p>

                                <input
                                    type="number"
                                    className="py-1 w-14 border-l border-gray-300 text-center text-sm bg-gray-200 font-bold placeholder:text-gray-900 placeholder:font-bold [&::-webkit-inner-spin-button]:block [&::-webkit-inner-spin-button]:opacity-100"
                                    placeholder="0"
                                    value={workoutConfig.unilateralSeconds}
                                    onChange={(event) => {
                                        HandleMicroVolumeConfigDraftChange(
                                            microVolumeConfigIndex,
                                            'unilateralSeconds',
                                            { workoutConfigIndex, seconds: event.target.value === '' ? '' : parseInt(event.target.value, 10) }
                                        )
                                    }}
                                />

                                <input
                                    type="number"
                                    className="py-1 w-14 border-l border-gray-300 text-center text-sm bg-gray-200 font-bold placeholder:text-gray-900 placeholder:font-bold [&::-webkit-inner-spin-button]:block [&::-webkit-inner-spin-button]:opacity-100"
                                    placeholder="0"
                                    value={workoutConfig.bilateralSeconds}
                                    onChange={(event) => {
                                        HandleMicroVolumeConfigDraftChange(
                                            microVolumeConfigIndex,
                                            'bilateralSeconds',
                                            { workoutConfigIndex, seconds: event.target.value === '' ? '' : parseInt(event.target.value, 10) }
                                        )
                                    }}
                                />
                            </div>

                            {/* Slot specific volume data */}

                            {workoutConfig.workoutPhaseConfigs.map((workoutPhase, workoutPhaseIndex) => (
                                <div key={workoutPhaseIndex}>
                                    <div className='flex items-center gap-x-2 bg-gray-100'>
                                        <p className='text-sm text-left flex-grow px-2'>
                                            {workoutPhase.name}
                                        </p>

                                        <p className='text-sm'>Sets</p>

                                        <input
                                            type="number"
                                            className="py-1 w-14 border-l border-gray-300 text-center text-sm bg-gray-100 font-bold placeholder:text-gray-900 placeholder:font-bold [&::-webkit-inner-spin-button]:block [&::-webkit-inner-spin-button]:opacity-100"
                                            placeholder="0"
                                            value={workoutPhase.sets}
                                            onChange={(event) => {
                                                HandleMicroVolumeConfigDraftChange(
                                                    microVolumeConfigIndex,
                                                    'sets',
                                                    { workoutConfigIndex, workoutPhaseIndex, sets: event.target.value === '' ? '' : parseInt(event.target.value, 10) }
                                                )
                                            }}
                                        />
                                    </div>

                                    {workoutPhase.workoutSlotConfigs.map((workoutSlotConfig, workoutSlotIndex) => (
                                        <NanoVolumeWorkoutSlotConfigEditor
                                            key={`${microVolumeConfigIndex}-${workoutConfigIndex}-${workoutPhaseIndex}-${workoutSlotIndex}`}
                                            microVolumeConfigIndex={microVolumeConfigIndex}
                                            workoutConfigIndex={workoutConfigIndex}
                                            workoutPhaseIndex={workoutPhaseIndex}
                                            workoutSlotIndex={workoutSlotIndex}
                                            workoutSlotConfig={workoutSlotConfig}
                                        />
                                    ))}  

                                    {/* {workoutPhaseIndex < workoutConfig.workoutPhaseConfigs.length - 1 &&
                                        <div className='w-full h-2 bg-gray-300' />
                                    } */}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </ContentFrame>
    )
}